import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronsLeft, ChevronsRight } from "lucide-react";

// TrackScroller — the storyboard's slide bar, and the "conveyor belt".
//
// Owner report 2026-10-05: the timeline's scrollbar and zoom slider were too
// small and fiddly, on a touch screen especially. This replaces the native
// scrollbar with a full-width, fat-thumbed slider bound to the track's scroll
// position, flanked by two HOLD buttons: press and hold and the strip glides
// past like a conveyor belt — slow at first, easing up to a comfortable speed
// — and stops the instant you let go.

/** px/s when a hold starts, the cruising speed, and how long the ease-up takes. */
export const CONVEYOR_START_PX_S = 80;
export const CONVEYOR_CRUISE_PX_S = 420;
const CONVEYOR_RAMP_MS = 1800;
/** How long a zoom's card animation can take to settle. */
export const SETTLE_MS = 700;

export function conveyorSpeed(heldMs: number): number {
  const k = Math.min(1, Math.max(0, heldMs / CONVEYOR_RAMP_MS));
  const eased = k * k * (3 - 2 * k); // smoothstep: no jerk at either end
  return CONVEYOR_START_PX_S + (CONVEYOR_CRUISE_PX_S - CONVEYOR_START_PX_S) * eased;
}

export default function TrackScroller({ trackRef, contentKey }: {
  trackRef: React.RefObject<HTMLDivElement | null>;
  /** Changes whenever the track's width may have changed (zoom, clip count). */
  contentKey: string;
}) {
  const { t } = useTranslation();
  const [pos, setPos] = useState(0);
  const [max, setMax] = useState(0);
  const [holding, setHolding] = useState<-1 | 1 | 0>(0);
  const raf = useRef<number | null>(null);

  const measure = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setMax(Math.max(0, el.scrollWidth - el.clientWidth));
    setPos(el.scrollLeft);
  }, [trackRef]);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    return () => el.removeEventListener("scroll", measure);
  }, [trackRef, measure]);
  // After a zoom the cards glide to their new size (framer's layout animation,
  // drawn with transforms that no ResizeObserver sees), so follow the strip's
  // width frame by frame until that settles.
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    measure();
    const until = performance.now() + SETTLE_MS;
    let frame = requestAnimationFrame(function tick(now) {
      measure();
      if (now < until) frame = requestAnimationFrame(tick);
    });
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => { cancelAnimationFrame(frame); ro?.disconnect(); };
  }, [trackRef, measure, contentKey]);

  const stop = useCallback(() => {
    if (raf.current !== null) cancelAnimationFrame(raf.current);
    raf.current = null;
    setHolding(0);
  }, []);
  useEffect(() => stop, [stop]);

  const start = useCallback((dir: -1 | 1) => {
    const el = trackRef.current;
    if (!el) return;
    if (raf.current !== null) cancelAnimationFrame(raf.current);
    setHolding(dir);
    const began = performance.now();
    let last = began;
    // A float position of our own: browsers round scrollLeft, which would
    // stall a slow belt at under a pixel per frame.
    let x = el.scrollLeft;
    const step = (now: number) => {
      const dt = Math.min(64, now - last) / 1000;
      last = now;
      const limit = el.scrollWidth - el.clientWidth;
      x = Math.min(limit, Math.max(0, x + dir * conveyorSpeed(now - began) * dt));
      el.scrollLeft = x;
      if ((dir < 0 && x <= 0) || (dir > 0 && x >= limit)) { stop(); return; }
      raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
  }, [trackRef, stop]);

  const holdProps = (dir: -1 | 1) => ({
    onPointerDown: (e: React.PointerEvent<HTMLButtonElement>) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* jsdom */ }
      start(dir);
    },
    onPointerUp: stop,
    onPointerCancel: stop,
    onLostPointerCapture: stop,
    onKeyDown: (e: React.KeyboardEvent) => { if ((e.key === " " || e.key === "Enter") && !e.repeat) { e.preventDefault(); start(dir); } },
    onKeyUp: (e: React.KeyboardEvent) => { if (e.key === " " || e.key === "Enter") stop(); },
    onBlur: stop,
    onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
  });

  const holdBtn = (dir: -1 | 1) =>
    `inline-flex h-11 min-w-11 shrink-0 select-none items-center justify-center gap-1 rounded-xl border px-3 text-xs font-bold transition-colors [touch-action:none] ${
      holding === dir ? "border-primary bg-primary/25 text-foreground" : "border-border bg-black/40 text-muted-foreground hover:text-foreground"}`;

  if (max <= 1) return null;
  return (
    // Holding a button (data-board-keep) or sliding the slider scrubs the strip and never moves the board
    // (boardHand; a finger swiping up or down from the slider moves it). The gaps between them are the card's
    // (review, 2026-10-09: the whole row held a finger on a phone, the gaps too). A finger is not a mouse there:
    // one dragged off a held button (data-board-hold) or from the slider's bar, not its thumb, moves the board.
    <div className="mt-2 flex items-center gap-2" data-testid="studio-track-scroller">
      <button type="button" aria-label={t("studio.timeline.conveyorBack")} title={t("studio.timeline.conveyorHint")}
        data-board-keep data-board-hold data-testid="studio-conveyor-back" className={holdBtn(-1)} {...holdProps(-1)}>
        <ChevronsLeft className="h-5 w-5" />
      </button>
      <input
        type="range" min={0} max={max} step={1} value={Math.min(pos, max)}
        onChange={(e) => { const el = trackRef.current; if (el) el.scrollLeft = Number(e.target.value); }}
        aria-label={t("studio.timeline.slideTimeline")} data-testid="studio-track-slider"
        className="studio-range min-w-0 flex-1"
      />
      <button type="button" aria-label={t("studio.timeline.conveyorForward")} title={t("studio.timeline.conveyorHint")}
        data-board-keep data-board-hold data-testid="studio-conveyor-forward" className={holdBtn(1)} {...holdProps(1)}>
        <ChevronsRight className="h-5 w-5" />
      </button>
    </div>
  );
}
